/**
 * Configuration Manager
 * Centralizes config.json and generates CSS custom properties
 * Eliminates duplication between config.json and styles.scss
 */

// Import config - Parcel handles JSON imports automatically
import config from '../../config.json';

/**
 * Generates CSS custom properties from config.json
 * This replaces the hardcoded CSS variables in styles.scss
 */
export const generateCSSVariables = () => {
  const cssVars = [];
  
  // Visual configuration
  if (config.visual) {
    // Colors
    if (config.visual.highlighting?.color) {
      cssVars.push(`--highlight-color: ${config.visual.highlighting.color}`);
    }
    if (config.visual.intervals?.color) {
      cssVars.push(`--bracket-color: ${config.visual.intervals.color}`);
    }
    
    // Layout spacing
    if (config.visual.layout?.scaleHeaderSpacing) {
      cssVars.push(`--scale-header-spacing: ${config.visual.layout.scaleHeaderSpacing}rem`);
    }
    
    // Animation settings
    if (config.visual.animation) {
      const anim = config.visual.animation;
      if (anim.noteGlowScale) cssVars.push(`--note-glow-scale: ${anim.noteGlowScale}`);
      if (anim.glowDuration) cssVars.push(`--glow-duration: ${anim.glowDuration}`);
      if (anim.shadowIntensity) cssVars.push(`--shadow-intensity: ${anim.shadowIntensity}px`);
    }
    
    // Interval highlighting
    if (config.visual.intervals?.highlighting) {
      const highlight = config.visual.intervals.highlighting;
      if (highlight.dimOpacity) cssVars.push(`--interval-dim-opacity: ${highlight.dimOpacity}`);
      if (highlight.highlightStrokeWidth) cssVars.push(`--interval-highlight-stroke: ${highlight.highlightStrokeWidth}`);
      if (highlight.transitionDuration) cssVars.push(`--interval-transition-duration: ${highlight.transitionDuration}`);
    }
  }
  
  // Typography configuration
  if (config.typography) {
    if (config.typography.scaleTitle) {
      const title = config.typography.scaleTitle;
      if (title.opacity) cssVars.push(`--scale-title-opacity: ${title.opacity}`);
      if (title.fontSize) cssVars.push(`--scale-title-font-size: ${title.fontSize}`);
    }
    if (config.typography.scaleName) {
      const name = config.typography.scaleName;
      if (name.opacity) cssVars.push(`--scale-name-opacity: ${name.opacity}`);
      if (name.fontSize) cssVars.push(`--scale-name-font-size: ${name.fontSize}`);
    }
  }
  
  return cssVars.join(';\n  ') + ';';
};

/**
 * Injects CSS variables into the document root
 * Call this once on app initialization
 */
export const applyCSSVariables = () => {
  const cssText = `:root {\n  ${generateCSSVariables()}\n}`;
  
  // Remove existing config-generated styles
  const existingStyle = document.getElementById('config-css-variables');
  if (existingStyle) {
    existingStyle.remove();
  }
  
  // Inject new styles
  const style = document.createElement('style');
  style.id = 'config-css-variables';
  style.textContent = cssText;
  document.head.appendChild(style);
};

/**
 * Get configuration values with type safety
 */
export const getConfig = () => config;

/**
 * Get specific config path with fallback
 */
export const getConfigValue = (path, fallback = null) => {
  const keys = path.split('.');
  let current = config;
  
  for (const key of keys) {
    if (current && typeof current === 'object' && key in current) {
      current = current[key];
    } else {
      return fallback;
    }
  }
  
  return current;
};

export default {
  generateCSSVariables,
  applyCSSVariables,
  getConfig,
  getConfigValue
};